import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import Tag from './Tag'

export enum LogicalOperator {
  And = 'and',
  Or = 'or',
}

export enum Intersection {
  Include = 'include',
  Exclude = 'exclude',
}

export enum ToggleableItemState {
  NotSelected = 0,
  Selected = 1,
  PartiallySelected = 2,
  Excluded = 3,
}

export interface FilterableDropdownItem {
  id: number | null
  name: string
  document_count?: number
  color?: string
  text_color?: string
  is_inbox_tag?: boolean
  depth?: number
  orderIndex?: number
}

const NEGATIVE_NULL_FILTER_VALUE = -999999

export class FilterableDropdownSelectionModel {
  manyToOne: boolean
  singleSelect: boolean
  private _logicalOperator: LogicalOperator = LogicalOperator.And
  temporaryLogicalOperator: LogicalOperator = this._logicalOperator
  private _intersection: Intersection = Intersection.Include
  temporaryIntersection: Intersection = this._intersection

  private _documentCounts: { [key: number]: number } = {}
  public set documentCounts(counts: { [key: number]: number }) {
    this._documentCounts = counts
  }

  private _items: FilterableDropdownItem[] = []
  get items(): FilterableDropdownItem[] {
    return this._items
  }

  set items(items: FilterableDropdownItem[]) {
    if (items) {
      this._items = items.map((item) => {
        const depth = (item as any).depth
        const orderIndex = (item as any).orderIndex
        const result = {
          ...item,
          depth: depth !== undefined ? depth : 0,
          orderIndex: orderIndex !== undefined ? orderIndex : 0,
        }
        return result
      })
      this.sortItems()
      this.setNullItem()
    }
  }

  private setNullItem() {
    if (this.manyToOne && this.logicalOperator === LogicalOperator.Or) {
      if (this._items[0]?.id === null) {
        this._items.shift()
      }
      return
    }

    const item = {
      name: 'Not assigned',
      id: this.manyToOne || this.intersection === Intersection.Include ? null : NEGATIVE_NULL_FILTER_VALUE,
    }

    if (this._items[0]?.id === null || this._items[0]?.id === NEGATIVE_NULL_FILTER_VALUE) {
      this._items[0] = item
    } else if (this._items) {
      this._items.unshift(item)
    }
  }

  constructor(manyToOne: boolean = false, singleSelect: boolean = false) {
    this.manyToOne = manyToOne
    this.singleSelect = singleSelect
  }

  private sortItems() {
    this._items.sort((a, b) => {
      if ((a.id === null && b.id !== null) || (a.id === NEGATIVE_NULL_FILTER_VALUE && b.id !== NEGATIVE_NULL_FILTER_VALUE)) {
        return -1
      } else if ((a.id !== null && b.id === null) || (a.id !== NEGATIVE_NULL_FILTER_VALUE && b.id !== NEGATIVE_NULL_FILTER_VALUE)) {
        return 1
      }

      // Preserve hierarchical order when provided (e.g., Tags)
      const ao = (a as any)['orderIndex']
      const bo = (b as any)['orderIndex']
      if (ao !== undefined && bo !== undefined) {
        return ao - bo
      } else if (
        this.getNonTemporary(a.id ?? 0) === ToggleableItemState.NotSelected &&
        this.getNonTemporary(b.id ?? 0) !== ToggleableItemState.NotSelected
      ) {
        return 1
      } else if (
        this.getNonTemporary(a.id ?? 0) !== ToggleableItemState.NotSelected &&
        this.getNonTemporary(b.id ?? 0) === ToggleableItemState.NotSelected
      ) {
        return -1
      } else if (
        Object.keys(this._documentCounts).length &&
        this.getDocumentCount(b.id ?? 0) === 0 &&
        this.getDocumentCount(a.id ?? 0) > this.getDocumentCount(b.id ?? 0)
      ) {
        return -1
      } else if (
        Object.keys(this._documentCounts).length &&
        this.getDocumentCount(a.id ?? 0) === 0 &&
        this.getDocumentCount(a.id ?? 0) < this.getDocumentCount(b.id ?? 0)
      ) {
        return 1
      } else {
        return a.name.localeCompare(b.name)
      }
    })
  }

  private selectionStates = new Map<number, ToggleableItemState>()
  private temporarySelectionStates = new Map<number, ToggleableItemState>()

  getSelectedItems() {
    return this.items.filter((i) => this.temporarySelectionStates.get(i.id ?? 0) === ToggleableItemState.Selected)
  }

  getExcludedItems() {
    return this.items.filter((i) => this.temporarySelectionStates.get(i.id ?? 0) === ToggleableItemState.Excluded)
  }

  set(id: number | null, state: ToggleableItemState, _fireEvent = true) {
    const key = id ?? 0
    if (state === ToggleableItemState.NotSelected) {
      this.temporarySelectionStates.delete(key)
    } else {
      this.temporarySelectionStates.set(key, state)
    }
  }

  toggle(id: number | null, _fireEvent = true) {
    const key = id ?? 0
    let state = this.temporarySelectionStates.get(key)

    // For !manyToOne filters with Include intersection, only allow single selection
    const isSingleSelectMode = !this.manyToOne && this.intersection === Intersection.Include

    if (state === undefined || (state !== ToggleableItemState.Selected && state !== ToggleableItemState.Excluded)) {
      if (this.manyToOne || this.singleSelect) {
        this.temporarySelectionStates.set(key, ToggleableItemState.Selected)

        if (this.singleSelect) {
          for (let k of this.temporarySelectionStates.keys()) {
            if (k != key) {
              this.temporarySelectionStates.delete(k)
            }
          }
        }
      } else {
        let newState = this.intersection === Intersection.Include ? ToggleableItemState.Selected : ToggleableItemState.Excluded
        if (!id) newState = ToggleableItemState.Selected
        this.temporarySelectionStates.set(key, newState)

        // If in single select mode (Include), clear all other selections
        if (isSingleSelectMode) {
          for (let k of this.temporarySelectionStates.keys()) {
            if (k !== key) {
              this.temporarySelectionStates.delete(k)
            }
          }
        }
      }
    } else if (state === ToggleableItemState.Selected || state === ToggleableItemState.Excluded) {
      this.temporarySelectionStates.delete(key)
    }

    if (!id) {
      for (let k of this.temporarySelectionStates.keys()) {
        if (k) {
          this.temporarySelectionStates.delete(k)
        }
      }
    } else {
      this.temporarySelectionStates.delete(0)
    }
  }

  exclude(id: number | null, _fireEvent: boolean = true) {
    const key = id ?? 0
    let state = this.temporarySelectionStates.get(key)
    if (id && (state === null || state === undefined || state !== ToggleableItemState.Excluded)) {
      this.temporaryLogicalOperator = this._logicalOperator = this.manyToOne ? LogicalOperator.And : LogicalOperator.Or

      if (this.manyToOne || this.singleSelect) {
        this.temporarySelectionStates.set(key, ToggleableItemState.Excluded)

        if (this.singleSelect) {
          for (let k of this.temporarySelectionStates.keys()) {
            if (k !== key) {
              this.temporarySelectionStates.delete(k)
            }
          }
        }
      } else {
        let newState = this.intersection === Intersection.Include ? ToggleableItemState.Selected : ToggleableItemState.Excluded
        if (state === ToggleableItemState.Selected && this.intersection === Intersection.Include) {
          newState = ToggleableItemState.NotSelected
        }
        this.temporarySelectionStates.set(key, newState)
      }
    } else if (!id || state === ToggleableItemState.Excluded) {
      this.temporarySelectionStates.delete(key)
    }
  }

  private getNonTemporary(id: number) {
    return this.selectionStates.get(id) || ToggleableItemState.NotSelected
  }

  get logicalOperator(): LogicalOperator {
    return this.temporaryLogicalOperator
  }

  set logicalOperator(operator: LogicalOperator) {
    this.temporaryLogicalOperator = operator
    this.setNullItem()
  }

  toggleOperator() {
    // This will be handled by the component
  }

  get intersection(): Intersection {
    return this.temporaryIntersection
  }

  set intersection(intersection: Intersection) {
    this.temporaryIntersection = intersection
    this.setNullItem()
  }

  toggleIntersection() {
    // Allow intersection toggle even when no items are selected
    if (this.temporarySelectionStates.size === 0) {
      // Just change the intersection state, no items to update
      return
    }

    let newState = this.intersection === Intersection.Include ? ToggleableItemState.Selected : ToggleableItemState.Excluded

    this.temporarySelectionStates.forEach((_state, key) => {
      if (key === 0 && this.intersection === Intersection.Exclude) {
        this.temporarySelectionStates.set(NEGATIVE_NULL_FILTER_VALUE, newState)
      } else if (key === NEGATIVE_NULL_FILTER_VALUE && this.intersection === Intersection.Include) {
        this.temporarySelectionStates.set(0, newState)
      } else {
        this.temporarySelectionStates.set(key, newState)
      }
    })
  }

  get(id: number | null) {
    const key = id ?? 0
    return this.temporarySelectionStates.get(key) || ToggleableItemState.NotSelected
  }

  selectionSize() {
    return this.getSelectedItems().length
  }

  get totalCount() {
    return this.getSelectedItems().length + this.getExcludedItems().length
  }

  clear(_fireEvent = true) {
    this.temporarySelectionStates.clear()
    this.temporaryLogicalOperator = this._logicalOperator = LogicalOperator.And
    this.temporaryIntersection = this._intersection = Intersection.Include
    this.setNullItem()
  }

  clearTemporarySelections() {
    this.temporarySelectionStates.clear()
  }

  isDirty() {
    if (!Array.from(this.temporarySelectionStates.keys()).every((id) => this.temporarySelectionStates.get(id) === this.selectionStates.get(id))) {
      return true
    } else if (!Array.from(this.selectionStates.keys()).every((id) => this.selectionStates.get(id) === this.temporarySelectionStates.get(id))) {
      return true
    } else if (this.temporaryLogicalOperator !== this._logicalOperator) {
      return true
    } else if (this.temporaryIntersection !== this._intersection) {
      return true
    } else {
      return false
    }
  }

  isNoneSelected() {
    return (
      (this.selectionSize() === 1 && this.get(null) === ToggleableItemState.Selected) ||
      (this.intersection === Intersection.Exclude && this.get(NEGATIVE_NULL_FILTER_VALUE) === ToggleableItemState.Excluded)
    )
  }

  getDocumentCount(id: number) {
    return this._documentCounts[id] || 0
  }

  init(map: Map<number, ToggleableItemState>) {
    this.temporarySelectionStates = map
    this.apply()
  }

  apply() {
    this.selectionStates.clear()
    this.temporarySelectionStates.forEach((value, key) => {
      this.selectionStates.set(key, value)
    })
    this._logicalOperator = this.temporaryLogicalOperator
    this._intersection = this.temporaryIntersection
    this.sortItems()
  }

  reset(complete: boolean = false) {
    this.temporarySelectionStates.clear()
    if (complete) {
      this.selectionStates.clear()
    } else {
      this.selectionStates.forEach((value, key) => {
        this.temporarySelectionStates.set(key, value)
      })
    }
  }

  diff(): { itemsToAdd: FilterableDropdownItem[]; itemsToRemove: FilterableDropdownItem[] } {
    return {
      itemsToAdd: this.items.filter(
        (item) =>
          this.temporarySelectionStates.get(item.id ?? 0) === ToggleableItemState.Selected &&
          this.selectionStates.get(item.id ?? 0) !== ToggleableItemState.Selected
      ),
      itemsToRemove: this.items.filter((item) => !this.temporarySelectionStates.has(item.id ?? 0) && this.selectionStates.has(item.id ?? 0)),
    }
  }
}

// ClearableBadge Component
interface ClearableBadgeProps {
  number: number
  selected: boolean
  onCleared: () => void
}

const ClearableBadge: React.FC<ClearableBadgeProps> = ({ number, selected, onCleared }) => {
  const { t } = useTranslation()
  const active = selected || number > -1
  const isNumbered = number > -1

  if (!active) return null

  return (
    <span
      className="position-absolute top-0 start-100 translate-middle badge bg-secondary border border-light rounded-pill p-1"
      title={t('clear')}
      onClick={(e) => {
        onCleared()
        e.stopPropagation()
        e.preventDefault()
      }}
      style={{ minWidth: '20px', minHeight: '20px', cursor: 'pointer' }}
    >
      {!isNumbered && selected && <i className="bi bi-check-lg" style={{ width: '1em', height: '1em' }}></i>}
      {isNumbered && (
        <div className="number" style={{ minWidth: '1em', minHeight: '1em', display: 'inline-block' }}>
          {number}
          <span className="visually-hidden">selected</span>
        </div>
      )}
      <i className="bi bi-x-lg" style={{ width: '.9em', height: '1em', display: 'none' }}></i>
    </span>
  )
}

// ToggleableDropdownButton Component
interface ToggleableDropdownButtonProps {
  item: FilterableDropdownItem
  state: ToggleableItemState
  count: number
  hideCount: boolean
  opacifyCount: boolean
  disabled: boolean
  onToggled: () => void
  onExclude: () => void
}

const ToggleableDropdownButton: React.FC<ToggleableDropdownButtonProps> = ({
  item,
  state,
  count,
  hideCount,
  opacifyCount,
  disabled,
  onToggled,
  onExclude,
}) => {
  const isTag = 'is_inbox_tag' in item
  const currentCount = count ?? item.document_count ?? 0
  const depth = (item as any).depth ?? 0

  const isChecked = () => state === ToggleableItemState.Selected
  const isPartiallyChecked = () => state === ToggleableItemState.PartiallySelected
  const isExcluded = () => state === ToggleableItemState.Excluded

  const toggleItem = (_event: React.MouseEvent) => {
    if (state === ToggleableItemState.Selected) {
      onExclude()
    } else {
      onToggled()
    }
  }

  return (
    <button
      className={`list-group-item list-group-item-action d-flex align-items-center p-2 border-top-0 border-start-0 border-end-0 border-bottom ${
        opacifyCount && !hideCount && currentCount === 0 ? 'opacity-50' : ''
      }`}
      role="menuitem"
      onClick={toggleItem}
      disabled={disabled}
      data-depth={isTag ? depth : 0}
      style={
        {
          '--item-depth': isTag ? depth + 1 : 1,
        } as React.CSSProperties
      }
    >
      <div className="selected-icon me-1" style={{ minWidth: '1em', minHeight: '1em' }}>
        {isChecked() && <i className="bi bi-check" style={{ width: '1em', height: '1em' }}></i>}
        {isPartiallyChecked() && <i className="bi bi-dash" style={{ width: '1em', height: '1em' }}></i>}
        {isExcluded() && <i className="bi bi-x" style={{ width: '1em', height: '1em' }}></i>}
      </div>
      <div
        className="me-1 name-cell"
        style={
          {
            '--depth': isTag ? (depth + 1).toString() : '1',
            paddingLeft: isTag ? `${Math.max(0, depth) * 1}rem` : undefined,
          } as React.CSSProperties
        }
      >
        {isTag && depth > 0 && <div className="indicator"></div>}
        <div>{isTag && item.id ? <Tag tagID={item.id} clickable={false} /> : <small>{item.name}</small>}</div>
      </div>
      {!hideCount && <div className="badge bg-light text-dark rounded-pill ms-auto me-1">{currentCount}</div>}
    </button>
  )
}

interface FilterableDropdownProps {
  title: string
  icon: string
  filterPlaceholder: string
  selectionModel: FilterableDropdownSelectionModel
  onSelectionModelChange: (model: FilterableDropdownSelectionModel) => void
  onOpened?: () => void
  onApply?: (changedItems: { itemsToAdd: FilterableDropdownItem[]; itemsToRemove: FilterableDropdownItem[] }) => void
  documentCounts?: { [key: number]: number }
  allowSelectNone?: boolean
  disabled?: boolean
  shortcutKey?: string
  className?: string
  editing?: boolean
  applyOnClose?: boolean
  createRef?: (name: string) => void
  extraButtonTitle?: string
  onExtraButton?: (changedItems: { itemsToAdd: FilterableDropdownItem[]; itemsToRemove: FilterableDropdownItem[] }) => void
  externalSelectionCount?: number
  panel?: boolean
  fullWidth?: boolean
}

export const FilterableDropdown: React.FC<FilterableDropdownProps> = ({
  title,
  icon,
  filterPlaceholder,
  selectionModel,
  onSelectionModelChange,
  onOpened,
  onApply,
  documentCounts: _documentCounts = {},
  allowSelectNone = true,
  disabled = false,
  className = '',
  editing = false,
  applyOnClose = false,
  createRef,
  extraButtonTitle,
  onExtraButton,
  externalSelectionCount,
  panel = false,
  fullWidth = true,
}) => {
  const { t } = useTranslation()
  const [filterText, setFilterText] = useState('')
  const [show, setShow] = useState(false)
  const [modelIsDirty, setModelIsDirty] = useState(false)
  const [keyboardIndex, setKeyboardIndex] = useState(-1)
  const [currentIntersection, setCurrentIntersection] = useState(selectionModel.intersection)
  const [, forceUpdate] = useState({})
  const filterInputRef = useRef<HTMLInputElement>(null)
  const buttonItemsRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const getInitialDropDirection = () => {
    if (typeof window === 'undefined') return 'down'
    return window.innerWidth >= 992 ? 'end' : 'down'
  }
  const [dropDirection, setDropDirection] = useState<'down' | 'end'>(panel ? getInitialDropDirection() : 'down')
  const [overlayContainer, setOverlayContainer] = useState<HTMLElement | null>(null)
  const selectionDisplayCount = externalSelectionCount !== undefined ? externalSelectionCount : selectionModel.totalCount
  const hasActiveSelection = !editing && selectionDisplayCount > 0

  useEffect(() => {
    if (!panel) return
    if (typeof document !== 'undefined') {
      setOverlayContainer(document.body)
    }
  }, [panel])

  useEffect(() => {
    if (!panel) return
    if (typeof window === 'undefined') return
    const mediaQuery = window.matchMedia('(min-width: 992px)')
    const updateDirection = () => {
      setDropDirection(mediaQuery.matches ? 'end' : 'down')
    }
    updateDirection()
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateDirection)
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(updateDirection)
    }
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', updateDirection)
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(updateDirection)
      }
    }
  }, [panel])

  const basePopperConfig = useMemo(
    () => ({
      strategy: 'fixed' as const,
      modifiers: [
        { name: 'applyStyles', enabled: false },
        { name: 'eventListeners', enabled: false },
      ],
    }),
    []
  )
  const popperConfig = panel ? basePopperConfig : undefined

  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 360 })

  const updateMenuPosition = useCallback(() => {
    if (!panel || typeof window === 'undefined') return
    const buttonRect = toggleRef.current?.getBoundingClientRect()
    const panelElement = document.querySelector('.documents-filters-panel') as HTMLElement | null
    const panelRect = panelElement?.getBoundingClientRect()

    if (!buttonRect) return

    const spacing = 16
    const baseTop = buttonRect.top + window.scrollY
    const panelRight = panelRect ? panelRect.right : buttonRect.right
    const baseLeft = panelRight + spacing

    const maxWidth = Math.min(420, window.innerWidth - spacing * 2)
    let left = baseLeft
    let width = maxWidth

    if (left + width > window.innerWidth - spacing) {
      left = window.innerWidth - width - spacing
    }
    if (left < spacing) {
      left = spacing
    }

    setMenuPosition({
      top: baseTop,
      left: left + window.scrollX,
      width,
    })
  }, [panel])

  useEffect(() => {
    if (!panel || !show) return
    updateMenuPosition()
    const handleUpdate = () => updateMenuPosition()
    window.addEventListener('resize', handleUpdate)
    window.addEventListener('scroll', handleUpdate, true)
    return () => {
      window.removeEventListener('resize', handleUpdate)
      window.removeEventListener('scroll', handleUpdate, true)
    }
  }, [show, panel, updateMenuPosition])

  useEffect(() => {
    if (!show) return
    const handleDocumentClick = (event: MouseEvent) => {
      const targetNode = event.target as Node
      if (menuRef.current?.contains(targetNode) || rootRef.current?.contains(targetNode)) {
        return
      }
      handleDropdownToggle(false)
    }
    document.addEventListener('mousedown', handleDocumentClick)
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const filteredItems = selectionModel.items.filter((item) => !filterText || item.name.toLowerCase().includes(filterText.toLowerCase()))

  const getModifierToggleEnabled = () => {
    return selectionModel.manyToOne ? selectionModel.selectionSize() > 1 && selectionModel.getExcludedItems().length === 0 : true
  }

  const getButtonVariant = () => {
    return !editing && selectionModel.selectionSize() > 0 ? 'primary' : 'outline-primary'
  }

  const getButtonText = () => {
    return (
      <>
        <span className="filter-chip__label">
          <i className={`bi bi-${icon}`}></i>
          <span>{title}</span>
        </span>
        {!editing && selectionDisplayCount > 0 && (
          <ClearableBadge
            number={selectionDisplayCount}
            selected={selectionModel.selectionSize() > 0}
            onCleared={() => {
              selectionModel.reset(true)
              onSelectionModelChange(selectionModel)
            }}
          />
        )}
      </>
    )
  }

  const handleToggle = (id: number | null) => {
    if (disabled) return
    selectionModel.toggle(id)
    setModelIsDirty(selectionModel.isDirty())
    onSelectionModelChange(selectionModel)
  }

  const handleExclude = (id: number | null) => {
    if (disabled) return
    if (editing) {
      selectionModel.toggle(id)
    } else {
      selectionModel.exclude(id)
    }
    setModelIsDirty(selectionModel.isDirty())
    onSelectionModelChange(selectionModel)
  }

  const handleLogicalOperatorChange = (operator: LogicalOperator) => {
    if (disabled || !getModifierToggleEnabled()) return
    selectionModel.logicalOperator = operator
    selectionModel.toggleOperator()
    onSelectionModelChange(selectionModel)
  }

  const handleIntersectionChange = (intersection: Intersection) => {
    if (disabled) return

    // When changing from Include to Exclude or vice versa,
    // we need to handle the selection state differently
    const previousIntersection = selectionModel.intersection

    // Get items BEFORE changing intersection
    const selectedItems = selectionModel.getSelectedItems()
    const excludedItems = selectionModel.getExcludedItems()

    // Now change the intersection
    selectionModel.intersection = intersection
    selectionModel.temporaryIntersection = intersection

    // If changing from Exclude to Include and we have multiple selections,
    // keep only the first one when switching to Include mode
    if (previousIntersection === Intersection.Exclude && intersection === Intersection.Include) {
      // In Include mode, only one selection is allowed for !manyToOne filters

      // Clear all current selections
      selectionModel.clearTemporarySelections()

      // If we had excluded items, convert first one to selected
      if (excludedItems.length > 0) {
        selectionModel.set(excludedItems[0].id, ToggleableItemState.Selected)
      } else if (selectedItems.length > 0) {
        // Keep only the first selected item
        selectionModel.set(selectedItems[0].id, ToggleableItemState.Selected)
      }
    } else {
      // For other transitions, use the standard toggle intersection
      selectionModel.toggleIntersection()
    }

    // Update local state to trigger re-render
    setCurrentIntersection(intersection)
    forceUpdate({})
    onSelectionModelChange(selectionModel)
  }

  const handleApply = () => {
    if (selectionModel.isDirty() && onApply) {
      const changes = selectionModel.diff()
      selectionModel.apply()
      onApply(changes)
    }
  }

  const handleCreate = () => {
    if (createRef) {
      createRef(filterText)
    }
  }

  const handleFilterEnter = () => {
    if (filteredItems.length === 1) {
      selectionModel.toggle(filteredItems[0].id)
      setTimeout(() => {
        if (editing) {
          handleApply()
        }
      }, 200)
    } else if (filteredItems.length === 0 && createRef) {
      handleCreate()
    }
  }

  const getUpdatedDocumentCount = (id: number | null) => {
    const count = selectionModel.getDocumentCount(id ?? 0)
    return count
  }

  const hideCount = (item: FilterableDropdownItem) => {
    return (
      selectionModel.logicalOperator === LogicalOperator.Or &&
      selectionModel.manyToOne &&
      selectionModel.get(item.id) !== ToggleableItemState.Selected
    )
  }

  const handleDropdownToggle = (nextShow: boolean, metadata?: { source?: string; originalEvent?: any }) => {
    if (!nextShow && metadata) {
      if (metadata.source === 'select') {
        return
      }
      const targetNode = metadata.originalEvent?.target as Node | null | undefined
      if (targetNode) {
        const clickedToggle = toggleRef.current?.contains(targetNode)
        const clickedInsideMenu = menuRef.current?.contains(targetNode)
        const clickedInsideRoot = rootRef.current?.contains(targetNode)
        if (!clickedToggle && clickedInsideMenu) {
          return
        }
        if (metadata.source === 'rootClose' && (clickedInsideMenu || clickedInsideRoot)) {
          return
        }
      }
      if (metadata.source === 'click' && !targetNode) {
        return
      }
    }

    setShow(nextShow)
    if (nextShow) {
      updateMenuPosition()
      setTimeout(() => {
        filterInputRef.current?.focus()
      }, 0)
      if (editing) {
        selectionModel.reset()
        setModelIsDirty(false)
      }
      selectionModel.singleSelect = editing && !selectionModel.manyToOne
      if (onOpened) {
        onOpened()
      }
    } else {
      setFilterText('')
      if (applyOnClose && selectionModel.isDirty() && onApply) {
        const changes = selectionModel.diff()
        selectionModel.apply()
        onApply(changes)
      }
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        if (event.target instanceof HTMLInputElement) {
          if (!filterText || (event.target as HTMLInputElement).selectionStart === filterText.length) {
            setKeyboardIndex(-1)
            focusNextButtonItem()
            event.preventDefault()
          }
        } else if (event.target instanceof HTMLButtonElement) {
          focusNextButtonItem()
          event.preventDefault()
        }
        break
      case 'ArrowUp':
        if (event.target instanceof HTMLButtonElement) {
          if (keyboardIndex === 0) {
            filterInputRef.current?.focus()
          } else {
            focusPreviousButtonItem()
          }
          event.preventDefault()
        }
        break
      case 'Tab':
        if (event.target instanceof HTMLInputElement) {
          setKeyboardIndex(0)
        } else if (event.target instanceof HTMLButtonElement) {
          if (event.shiftKey) {
            if (keyboardIndex > 0) {
              focusPreviousButtonItem(false)
            }
          } else {
            focusNextButtonItem(false)
          }
        }
        break
      default:
        break
    }
  }

  const focusNextButtonItem = (setFocus: boolean = true) => {
    setKeyboardIndex(Math.min(filteredItems.length - 1, keyboardIndex + 1))
    if (setFocus) setButtonItemFocus()
  }

  const focusPreviousButtonItem = (setFocus: boolean = true) => {
    setKeyboardIndex(Math.max(0, keyboardIndex - 1))
    if (setFocus) setButtonItemFocus()
  }

  const setButtonItemFocus = () => {
    if (buttonItemsRef.current && buttonItemsRef.current.children[keyboardIndex]) {
      ;(buttonItemsRef.current.children[keyboardIndex] as HTMLElement).focus()
    }
  }

  useEffect(() => {
    setModelIsDirty(selectionModel.isDirty())
    setCurrentIntersection(selectionModel.intersection)
  }, [selectionModel])

  useEffect(() => {
    if (_documentCounts && Object.keys(_documentCounts).length > 0) {
      selectionModel.documentCounts = _documentCounts
    }
  }, [_documentCounts, selectionModel, title])

  const rootClassName = ['btn-group', fullWidth ? 'w-100' : '', className || '', hasActiveSelection ? 'filter-chip--active' : '']
    .filter(Boolean)
    .join(' ')

  return (
    <div className={rootClassName} onKeyDown={handleKeyDown} ref={rootRef}>
      <Dropdown
        show={show}
        onToggle={handleDropdownToggle}
        className={fullWidth ? 'w-100' : ''}
        drop={panel ? dropDirection : undefined}
        autoClose={false}
      >
        <Dropdown.Toggle
          variant={getButtonVariant()}
          size="sm"
          disabled={disabled}
          id={`dropdown_${title?.replace(/\s/g, '_').toLowerCase()}`}
          className={`${fullWidth ? 'w-100 ' : ''}d-flex align-items-center justify-content-between text-start`}
          style={{ whiteSpace: 'nowrap' }}
          ref={toggleRef}
        >
          {getButtonText()}
        </Dropdown.Toggle>

        <Dropdown.Menu
          className={`py-0 shadow filter-dropdown-menu ${panel ? 'filter-dropdown-menu--panel' : ''}`}
          ref={menuRef}
          style={
            panel
              ? {
                  position: 'fixed',
                  top: `${menuPosition.top}px`,
                  left: `${menuPosition.left}px`,
                  width: `${menuPosition.width}px`,
                  minWidth: `${menuPosition.width}px`,
                  transform: 'none',
                }
              : undefined
          }
          popperConfig={panel ? popperConfig : undefined}
        >
          {panel && (
            <div className="filter-panel-header px-3 py-2 border-bottom">
              <div>
                <p className="filter-panel-header__title mb-0">{title}</p>
                <p className="filter-panel-header__subtitle mb-0 text-muted">{filterPlaceholder}</p>
              </div>
              <button type="button" className="btn btn-sm btn-outline-secondary filter-panel-close" onClick={() => handleDropdownToggle(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          )}
          <div className={panel ? 'filter-panel-body p-3' : undefined}>
            <div className="list-group list-group-flush">
              {/* Logical Operator Toggle for manyToOne */}
              {!editing && selectionModel.manyToOne && (
                <div className="list-group-item d-flex">
                  <div
                    className="btn-group btn-group-xs flex-fill"
                    role="group"
                    style={
                      {
                        height: '24px',
                        '--bs-btn-active-bg': 'var(--bs-primary)',
                        '--bs-btn-active-border-color': 'var(--bs-primary)',
                        '--bs-btn-active-color': 'var(--pngx-primary-text-contrast)',
                      } as React.CSSProperties
                    }
                  >
                    <input
                      type="radio"
                      className="btn-check"
                      id={`logicalOperatorAnd_${title?.replace(/\s/g, '_').toLowerCase()}`}
                      name={`logicalOperator_${title?.replace(/\s/g, '_').toLowerCase()}`}
                      value="and"
                      checked={selectionModel.logicalOperator === LogicalOperator.And}
                      disabled={!getModifierToggleEnabled()}
                      onChange={() => handleLogicalOperatorChange(LogicalOperator.And)}
                    />
                    <label
                      className="btn btn-outline-primary filter-toggle-btn"
                      htmlFor={`logicalOperatorAnd_${title?.replace(/\s/g, '_').toLowerCase()}`}
                      style={{
                        fontSize: '0.75rem',
                        padding: '2px 8px',
                        lineHeight: '1.2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {t('all')}
                    </label>
                    <input
                      type="radio"
                      className="btn-check"
                      id={`logicalOperatorOr_${title?.replace(/\s/g, '_').toLowerCase()}`}
                      name={`logicalOperator_${title?.replace(/\s/g, '_').toLowerCase()}`}
                      value="or"
                      checked={selectionModel.logicalOperator === LogicalOperator.Or}
                      disabled={!getModifierToggleEnabled()}
                      onChange={() => handleLogicalOperatorChange(LogicalOperator.Or)}
                    />
                    <label
                      className="btn btn-outline-primary filter-toggle-btn"
                      htmlFor={`logicalOperatorOr_${title?.replace(/\s/g, '_').toLowerCase()}`}
                      style={{
                        fontSize: '0.75rem',
                        padding: '2px 8px',
                        lineHeight: '1.2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {t('any')}
                    </label>
                  </div>
                </div>
              )}

              {/* Intersection Toggle for !manyToOne */}
              {!editing && !selectionModel.manyToOne && (
                <div className="list-group-item d-flex">
                  <div
                    className="btn-group btn-group-xs flex-fill"
                    role="group"
                    style={
                      {
                        height: '24px',
                        '--bs-btn-active-bg': 'var(--bs-primary)',
                        '--bs-btn-active-border-color': 'var(--bs-primary)',
                        '--bs-btn-active-color': 'var(--pngx-primary-text-contrast)',
                      } as React.CSSProperties
                    }
                  >
                    <input
                      type="radio"
                      className="btn-check"
                      id={`intersectionInclude_${title?.replace(/\s/g, '_').toLowerCase()}`}
                      name={`intersection_${title?.replace(/\s/g, '_').toLowerCase()}`}
                      value="include"
                      checked={currentIntersection === Intersection.Include}
                      disabled={!getModifierToggleEnabled()}
                      onChange={() => handleIntersectionChange(Intersection.Include)}
                    />
                    <label
                      className="btn btn-outline-primary filter-toggle-btn"
                      htmlFor={`intersectionInclude_${title?.replace(/\s/g, '_').toLowerCase()}`}
                      style={{
                        fontSize: '0.75rem',
                        padding: '2px 8px',
                        lineHeight: '1.2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {t('include')}
                    </label>
                    <input
                      type="radio"
                      className="btn-check"
                      id={`intersectionExclude_${title?.replace(/\s/g, '_').toLowerCase()}`}
                      name={`intersection_${title?.replace(/\s/g, '_').toLowerCase()}`}
                      value="exclude"
                      checked={currentIntersection === Intersection.Exclude}
                      disabled={!getModifierToggleEnabled()}
                      onChange={() => handleIntersectionChange(Intersection.Exclude)}
                    />
                    <label
                      className="btn btn-outline-primary filter-toggle-btn"
                      htmlFor={`intersectionExclude_${title?.replace(/\s/g, '_').toLowerCase()}`}
                      style={{
                        fontSize: '0.75rem',
                        padding: '2px 8px',
                        lineHeight: '1.2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {t('exclude')}
                    </label>
                  </div>
                </div>
              )}

              {/* Search Input */}
              <div className="list-group-item">
                <div className="input-group input-group-sm">
                  <input
                    ref={filterInputRef}
                    className="form-control"
                    type="text"
                    spellCheck="false"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    onKeyUp={(e) => {
                      if (e.key === 'Enter') {
                        handleFilterEnter()
                      }
                    }}
                    placeholder={filterPlaceholder}
                  />
                </div>
              </div>

              {/* Items List */}
              {selectionModel.items && (
                <div className="items" ref={buttonItemsRef} style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {filteredItems.map((item) => {
                    if (!allowSelectNone && !item.id) return null

                    return (
                      <ToggleableDropdownButton
                        key={item.id}
                        item={item}
                        hideCount={hideCount(item)}
                        opacifyCount={!editing}
                        state={selectionModel.get(item.id)}
                        count={getUpdatedDocumentCount(item.id)}
                        onToggled={() => handleToggle(item.id)}
                        onExclude={() => handleExclude(item.id)}
                        disabled={disabled}
                      />
                    )
                  })}
                </div>
              )}

              {/* Create Button */}
              {editing && (
                <>
                  {filteredItems.length === 0 && createRef && (
                    <button className="list-group-item list-group-item-action bg-light" onClick={handleCreate} disabled={disabled}>
                      <small className="ms-2">
                        {t('create')} "{filterText}"
                      </small>
                      <i className="bi bi-plus" style={{ width: '1.5em', height: '1em' }}></i>
                    </button>
                  )}
                  {filteredItems.length > 0 && (
                    <button
                      className={`list-group-item list-group-item-action d-flex align-items-center ${
                        modelIsDirty ? 'text-white' : 'bg-light text-muted'
                      }`}
                      onClick={handleApply}
                      disabled={!modelIsDirty || disabled}
                      style={{
                        backgroundColor: modelIsDirty && !disabled ? 'var(--bs-primary)' : undefined,
                        cursor: modelIsDirty && !disabled ? 'pointer' : 'not-allowed',
                        opacity: modelIsDirty && !disabled ? 1 : 0.6,
                      }}
                    >
                      <small className={`ms-2 ${modelIsDirty ? 'fw-bold' : ''}`}>{t('apply')}</small>
                      <i className="bi bi-arrow-right ms-auto" style={{ width: '1.5em', height: '1em' }}></i>
                    </button>
                  )}
                </>
              )}

              {/* Extra Button */}
              {extraButtonTitle && onExtraButton && (
                <button
                  className="list-group-item list-group-item-action bg-light d-flex align-items-center"
                  onClick={() => {
                    if (onExtraButton) {
                      const changes = selectionModel.diff()
                      onExtraButton(changes)
                    }
                  }}
                  disabled={disabled}
                >
                  <small className="ms-2 fw-bold">{extraButtonTitle}</small>
                  <i className="bi bi-arrow-right" style={{ width: '1.5em', height: '1em' }}></i>
                </button>
              )}

              {/* Help Text for manyToOne */}
              {!editing && selectionModel.manyToOne && (
                <div className="list-group-item list-group-item-note pt-1 pb-2" style={{ lineHeight: 1 }}>
                  <small style={{ fontSize: '65%' }}>{t('click_again_to_exclude_items')}</small>
                </div>
              )}
            </div>
          </div>
        </Dropdown.Menu>
      </Dropdown>

      {panel &&
        show &&
        overlayContainer &&
        createPortal(<div className="filter-panel-overlay" onClick={() => handleDropdownToggle(false)}></div>, overlayContainer)}
    </div>
  )
}

export default FilterableDropdown
